import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { useRequest } from 'api';
import { useTranslationX } from 'i18n';
import { Event, GameData, PlayData, TeamData } from 'types';
import { Overlay } from 'components';
import { IconButton, Loader } from 'components/ui';
import { VS, X } from 'assets/vector';
import { classes, parseClock, parsePeriod } from 'utils';

import textBg from 'assets/images/play_item_text_bg.png';
import scoreBg from 'assets/images/play_item_score_bg.png';

import styles from './PlayByPlay.module.scss';

type Props = {
  events: Event[];
  game: GameData;
  onClose: () => void;
};

const Content = ({ events, game, onClose, size }: Props & { size?: number }) => {
  const [plays, setPlays] = useState<PlayData[]>();

  const request = useRequest();
  const { tx } = useTranslationX('play.plays');

  useEffect(() => {
    if (events.length) {
      const promises: Promise<PlayData[]>[] = [];

      for (const { id } of events) {
        promises.push(
          new Promise<PlayData[]>(resolve => {
            request<PlayData[]>({
              url: `/v1/plays/${id}`,
              onSuccess: resolve,
              onError: () => resolve([]),
            });
          })
        );
      }

      Promise.all(promises).then(result => {
        const data: PlayData[] = [];
        result.forEach(item => data.push(...item));
        setPlays(data);
      });
    }
  }, [events, request]);

  return (
    <div className={classes(styles.playByPlay, isMobile ? styles.mobile : styles.web)}>
      <div className={styles.header}>
        <span className={styles.title}>{tx('title')}</span>
        <IconButton icon={X} onClick={onClose} />
      </div>

      <div className={styles.content}>
        {plays?.length ? (
          <div className={styles.wrapper}>
            {plays.map((item, index) => renderItem(item, game, index, size))}
          </div>
        ) : null}

        {plays && !plays.length ? (
          <div className={styles.empty}>{tx('empty')}</div>
        ) : null}

        <Loader visible={!plays} />
      </div>
    </div>
  );
};

const PlayByPlay = ({ events, game, onClose }: Props) => {
  const [visible, setVisible] = useState(true);
  const close = useCallback(() => setVisible(false), []);

  return (
    <Overlay
      visible={visible}
      onClose={onClose}
      ratio={1.5}
      style={{
        backgroundColor: styles.bgColor,
        borderRadius: isMobile ? 0 : styles.radius,
      }}
    >
      {size => <Content onClose={close} events={events} game={game} size={size?.width} />}
    </Overlay>
  );
};

export default PlayByPlay;

const renderItem = (data: PlayData, game: GameData, index: number, size = 0) => {
  switch (data.type) {
    case 'text': {
      const clock = parseClock(data.clock);
      const period = parsePeriod(data.period);

      return (
        <div
          key={index}
          className={classes(styles.item, styles.text)}
          style={{ backgroundImage: `url(${textBg})` }}
        >
          <div className={styles.header}>{`${clock}${period ? ' - ' : ''}${period}`}</div>
          <div className={styles.body}>{data.text}</div>
        </div>
      );
    }

    case 'score': {
      const { homePosition } = game;
      let leftTeam, rightTeam;

      if (homePosition === 'left') {
        leftTeam = { ...game.home, score: data.homeScore };
        rightTeam = { ...game.away, score: data.awayScore };
      } else {
        rightTeam = { ...game.home, score: data.homeScore };
        leftTeam = { ...game.away, score: data.awayScore };
      }

      return (
        <div
          key={index}
          className={classes(styles.item, styles.score)}
          style={{ backgroundImage: `url(${scoreBg})` }}
        >
          {renderTeam(leftTeam, false, size)}

          <div className={styles.vs}>
            <VS />
          </div>

          {renderTeam(rightTeam, true, size)}
        </div>
      );
    }

    case 'trigger': {
      const { logoUrl, name, status } = data.interaction;

      return (
        <div
          key={index}
          className={classes(styles.item, styles.trigger)}
          style={status !== 'active' ? { opacity: 0.6 } : undefined}
        >
          <div style={logoUrl ? { backgroundImage: `url(${logoUrl})` } : undefined} />
          <span className={styles.text}>{name}</span>
        </div>
      );
    }

    default:
      return null;
  }
};

const renderTeam = ({ name, logo, score }: TeamData, reverse: boolean, size: number) => (
  <div className={classes(styles.team, reverse ? styles.reverse : '')}>
    <div className={styles.wrapper}>
      <div
        className={styles.name}
        style={size ? { fontSize: `${size * 0.025}px` } : undefined}
      >
        {name}
      </div>

      <div
        className={styles.logo}
        style={logo ? { backgroundImage: `url(${logo})` } : undefined}
      />
    </div>

    <div className={styles.score}>
      <span>{typeof score === 'number' ? score || 0 : '-'}</span>
    </div>
  </div>
);
